/**
 * Sprint 154 — Slugs per idioma. FONT ÚNICA.
 *
 * PER QUÈ. Fins ara cada calculadora tenia UN slug per a les sis llengües:
 * un alemany que busca «pool volumen berechnen» aterrava a
 * /de/tools/pool-volume-calculator — una URL anglesa amb contingut alemany.
 * El títol i la descripció ja parlen la seva llengua des del Sprint 152;
 * la URL, no. I la URL surt a la SERP en negreta sobre els termes de la
 * consulta.
 *
 * QUÈ ÉS `defaultLang`. L'idioma que viu a la ruta SENSE prefix. Per a la
 * major part del lloc és el castellà; per a les tres de /tools/ és l'anglès,
 * que és on tenen les impressions i l'historial. Aquell slug NO es toca: és
 * l'únic que ja posiciona.
 *
 * REGLA. Els slugs que ja tenen historial no es canvien mai. Els que
 * s'estrenen aquí (de, nl, fr, it de cada calculadora) fins ahir servien
 * metadades en anglès, o sigui que no posicionen per res i no hi ha res a
 * perdre movent-los.
 *
 * Qui ho consumeix — tot surt d'aquí i no es duplica enlloc:
 *   · LangRoute.localizePath  — enllaços interns i canvi d'idioma
 *   · App.js                  — generació de rutes
 *   · SEO.js                  — canonical i hreflang de client
 *   · build-seo-manifest.js   — metadades i hreflang de servidor
 *   · server.js               — 301 dels slugs antics
 */

export const SUPPORTED = ['es', 'en', 'fr', 'de', 'it', 'nl'];

export const ROUTE_SLUGS = {
  // ─── les tres de /tools/: l'anglès és el que mana i no es toca ───
  // Sprint 161 — Volum i energia estrenen versió castellana pròpia. Fins ara
  // /tools/... es declarava anglesa (títol del servidor, hreflang 'en' i
  // x-default) però es RENDERITZAVA en castellà, perquè l'idioma de l'app
  // surt de la URL i sense prefix era 'es'. Ara /tools/... és anglesa de
  // debò i el castellà viu al seu slug, també sense prefix.
  // L'aigua encara no: congelada pel Bloc A fins al 05/10.
  volume: {
    defaultLang: 'en',
    slugs: {
      en: 'tools/pool-volume-calculator',
      es: 'calculadora-volumen-piscina',
      de: 'poolvolumen-berechnen',
      nl: 'zwembad-inhoud-berekenen',
      fr: 'calcul-volume-piscine',
      it: 'calcolo-volume-piscina',
    },
  },
  energy: {
    defaultLang: 'en',
    slugs: {
      en: 'tools/pool-energy-calculator',
      es: 'calculadora-consumo-electrico-piscina',
      de: 'pool-stromverbrauch-berechnen',
      nl: 'zwembad-stroomverbruik-berekenen',
      fr: 'consommation-electrique-piscine',
      it: 'consumo-elettrico-piscina',
    },
  },
  // Sprint 166 — L'aigua tanca el trio: quedava fora perquè el Bloc A la va
  // congelar del 21 al 26/09. Tancat l'experiment, entra al mateix esquema
  // que volum i energia.
  water: {
    defaultLang: 'en',
    slugs: {
      en: 'tools/pool-water-calculator',
      es: 'calculadora-consumo-agua-piscina',
      de: 'pool-wasserverbrauch-berechnen',
      nl: 'zwembad-waterverbruik-berekenen',
      fr: 'consommation-eau-piscine',
      it: 'consumo-acqua-piscina',
    },
  },

  // ─── les cinc de slug castellà ───
  salt: {
    defaultLang: 'es',
    slugs: {
      es: 'calculadora-sal-piscina',
      en: 'pool-salt-calculator',
      de: 'salzmenge-pool-berechnen',
      nl: 'zoutberekening-zwembad',
      fr: 'calcul-sel-piscine',
      it: 'calcolo-sale-piscina',
    },
  },
  chlorine: {
    defaultLang: 'es',
    slugs: {
      es: 'calculadora-cloro-piscina',
      en: 'pool-chlorine-calculator',
      de: 'chlorbedarf-pool-berechnen',
      nl: 'chloor-zwembad-berekenen',
      fr: 'calcul-chlore-piscine',
      it: 'calcolo-cloro-piscina',
    },
  },
  filtration: {
    defaultLang: 'es',
    slugs: {
      es: 'calculadora-tiempo-filtracion',
      en: 'pool-filtration-time-calculator',
      de: 'filterlaufzeit-pool-berechnen',
      nl: 'filtertijd-zwembad-berekenen',
      fr: 'temps-filtration-piscine',
      it: 'tempo-filtrazione-piscina',
    },
  },
  heatpump: {
    defaultLang: 'es',
    slugs: {
      es: 'calculadora-bomba-calor-piscina',
      en: 'pool-heat-pump-calculator',
      de: 'waermepumpe-pool-berechnen',
      nl: 'warmtepomp-zwembad-berekenen',
      fr: 'calcul-pompe-chaleur-piscine',
      it: 'calcolo-pompa-calore-piscina',
    },
  },
  pool_base: {
    defaultLang: 'es',
    slugs: {
      // Nomes ES a la v1: els textos encara no estan traduits i publicar
      // un slug per idioma que serveix castella seria el defecte que vam
      // arreglar al setembre.
      es: 'calculadora-base-piscina-elevada',
    },
  },
  cover_config: {
    defaultLang: 'es',
    slugs: {
      // Nomes ES: textos en castella i normativa espanyola (CTE).
      es: 'configurador-cubierta-piscina',
    },
  },
  solar: {
    defaultLang: 'es',
    slugs: {
      es: 'calculadora-placas-solares-piscina',
      en: 'pool-solar-panel-calculator',
      de: 'solarabsorber-pool-berechnen',
      nl: 'zonnepanelen-zwembad-berekenen',
      fr: 'panneaux-solaires-piscine',
      it: 'pannelli-solari-piscina',
    },
  },
};

/**
 * Ruta pública d'una secció en un idioma. null si no existeix en aquell idioma.
 * Sense prefix: l'idioma per defecte de la secció i, sempre, el castellà
 * (/es/<ruta> fa 301 a /<ruta> a tot el lloc).
 */
export function pathFor(key, lang) {
  const spec = ROUTE_SLUGS[key];
  if (!spec) return null;
  const slug = spec.slugs[lang];
  if (!slug) return null;
  return lang === spec.defaultLang || lang === 'es' ? `/${slug}` : `/${lang}/${slug}`;
}

/**
 * Idioma d'una ruta SENSE prefix que pertany a una secció amb slug per
 * idioma: /tools/pool-volume-calculator → 'en'. null si la ruta no és cap
 * secció, o si la secció no té versió castellana pròpia (aigua): en aquest
 * cas la ruta sense prefix segueix sent la castellana, com abans.
 */
export function langOfSlugPath(path) {
  const hit = resolvePath(path);
  if (!hit || !ROUTE_SLUGS[hit.key].slugs.es) return null;
  return hit.lang;
}

/** Totes les rutes d'una secció, { lang: path }. */
export function pathsFor(key) {
  const spec = ROUTE_SLUGS[key];
  if (!spec) return {};
  const out = {};
  for (const lang of Object.keys(spec.slugs)) out[lang] = pathFor(key, lang);
  return out;
}

// Índex invers path -> { key, lang }. Es construeix un cop.
const BY_PATH = {};
for (const key of Object.keys(ROUTE_SLUGS)) {
  for (const [lang, p] of Object.entries(pathsFor(key))) BY_PATH[p] = { key, lang };
}

/** { key, lang } d'una ruta, o null si no és cap secció amb slug per idioma. */
export function resolvePath(path) {
  if (!path) return null;
  const cut = path.search(/[?#]/);
  const bare = (cut === -1 ? path : path.slice(0, cut)).replace(/\/$/, '') || '/';
  return BY_PATH[bare] || null;
}

/**
 * Slugs ANTICS que cal redirigir. Fins al Sprint 154 totes les llengües
 * compartien el slug de la seva secció, així que /de/calculadora-sal-piscina
 * i companyia existeixen i poden estar indexades.
 * Es genera: per cada secció, el slug del defaultLang prefixat amb cada
 * altre idioma.
 */
export function legacyRedirects() {
  const out = [];
  for (const key of Object.keys(ROUTE_SLUGS)) {
    const spec = ROUTE_SLUGS[key];
    const base = spec.slugs[spec.defaultLang];
    for (const lang of Object.keys(spec.slugs)) {
      if (lang === spec.defaultLang) continue;
      const from = `/${lang}/${base}`;
      const to = pathFor(key, lang);
      if (from !== to) out.push({ from, to });
    }
  }
  return out;
}
